import React from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { RadioGroup } from '~/eds/src/components/RadioGroup/RadioGroup';
import { Select } from '~/eds/src/components/Select/Select';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { typedMemo } from '~/eds/src/utils/typedMemo';
import { Nullable } from '~/types/Nullable';

import { DataFieldType } from '../../../enums/DataFieldType';
import { DataFieldValue } from '../types';
import { FieldData } from './types';
import { testEnableSort } from './utils';

interface Props<T extends DataFieldType> {
  fieldData: FieldData<T>;
  value: Nullable<DataFieldValue<T>>;
  onChange: (updatedValue: Nullable<DataFieldValue<T>>) => void;
}

export const DefaultFieldValue = typedMemo(
  <T extends DataFieldType>({ fieldData, onChange, value }: Props<T>) => {
    const intl = useIntl();
    const { input, inputProps } = getInput(fieldData, intl);

    if (input === null) {
      return null;
    }

    return (
      <FormField<DataFieldValue<T>, false>
        optional
        label={intl.formatMessage({
          id: 'DefaultFieldValue.label',
          defaultMessage: 'Default Value',
          description: 'Label for default value input',
        })}
        name={`${fieldData.fieldType} default value`}
        input={input}
        inputProps={inputProps}
        value={value}
        onChange={onChange}
      />
    );
  },
);

const getInput = <T extends DataFieldType>(
  fieldData: FieldData<T>,
  intl: IntlShape,
) => {
  const { fieldConfig, fieldType } = fieldData;

  let input;
  let inputProps;
  switch (fieldType) {
    case DataFieldType.ARRAY_MULTIPLE:
    case DataFieldType.ARRAY_SINGLE: {
      const { enableMultiple, optionsConfig, optionValues } = fieldConfig;
      const disabled = optionValues.length === 0;
      const placeholder = disabled
        ? intl.formatMessage({
            id: 'DefaultFieldValue.specifyValues',
            defaultMessage: 'Please specify field value(s)',
            description: 'Placeholder when no field values are specified',
          })
        : intl.formatMessage({
            id: 'DefaultFieldValue.selectDefault',
            defaultMessage: 'Select default value',
            description: 'Placeholder for selecting default value',
          });

      input = Select;
      inputProps = {
        disabled,
        enableSelectAll: false,
        enableSort: testEnableSort(optionsConfig),
        isMulti: enableMultiple,
        options: optionValues.map((value) => ({ label: value, value })),
        placeholder: placeholder,
      };
      break;
    }
    case DataFieldType.ATTACHMENT:
      input = null;
      break;
    case DataFieldType.BOOLEAN:
      input = RadioGroup;
      inputProps = {
        options: [
          {
            label: intl.formatMessage({
              id: 'DefaultFieldValue.true',
              defaultMessage: 'True',
              description: 'Label for true boolean option',
            }),
            value: true,
          },
          {
            label: intl.formatMessage({
              id: 'DefaultFieldValue.false',
              defaultMessage: 'False',
              description: 'Label for false boolean option',
            }),
            value: false,
          },
        ],
      };
      break;
    // TODO: DataFieldType.CLAUSE to be supported
    case DataFieldType.DATE:
      input = DateInput;
      break;
    case DataFieldType.NUMBER:
      input = NumberInput;
      break;
    case DataFieldType.TEXT_AREA:
      input = TextArea;
      break;
    case DataFieldType.STRING:
    default:
      input = TextInput;
      break;
  }

  return {
    input,
    inputProps,
  };
};
